import React, { Fragment, useState, useEffect } from 'react'
import { useQuery } from 'react-apollo-hooks'
import { saveAs } from 'file-saver'
import styled from 'react-emotion'
import moment from 'moment'
import { Svg, Modal, Tooltip, ga, fetchr } from '../../application'
import { tests, settings, urls } from '../../resources'
import { ShadowBox, colors } from '../../styles'
import getText from '../strings'
import SelfServiceCases from './SelfServiceCases'
import SelfServiceNewCase from './SelfServiceNewCase'
import HotelContactDetails from './HotelContactDetails'
import * as Queries from '../queries'
import TravelDocuments from './TravelDocuments'
import CancelBooking from './CancelBooking'
import { useMutation } from 'react-apollo-hooks'
import * as Mutations from '../mutations'
import GuideOnline from './GuideOnline'

const Container = styled(ShadowBox)`
  display: flex;
  flex-wrap: wrap;
  margin: unset;
  margin-top: -9px;
  margin-bottom: 10px;
  width: 100%;
  height: auto;
  padding: 10px;
  border-radius: 4px;
  position: relative;
  color: ${colors.tui_dark_blue};

  @media (max-width: 767px) {
    flex-direction: column;
  }
`

const Button = styled('div')`
  display: flex;
  align-items: center;
  line-height: 1em;
  background-color: ${colors.tui_beige_25};
  border-radius: 4px;
  padding: 10px;
  margin-right: 20px;
  cursor: pointer;
  position: relative;
  .pictogram {
    margin-right: 5px;
  }
  @media (max-width: 767px) {
    justify-content: center;
    padding: 10px 0;
    margin-right: 0px;
    margin-top: 5px;
  }
  svg {
    color: ${colors.tui_green};
    height: 18px;
  }
`

const NotificationCount = styled('span')`
  background-color: ${colors.tui_red};
  color: ${colors.tui_white};
  position: absolute;
  top: -8px;
  right: -8px;
  height: 16px;
  width: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
  font-weight: bold;
`

const CancellingDiv = styled('div')`
  display: flex;
  align-items: center;
  line-height: 1em;
  margin-right: 20px;
  img {
    margin-right: 10px;
  }
`

export default ({ booking, atiFilledIn, cancelledCallback }) => {
  const [isAvailable, setIsAvailable] = useState(false)
  const [atiStatus, setAtiStatus] = useState(null)
  const [showCasesModal, setShowCasesModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showHotelModal, setShowHotelModal] = useState(false)
  const [showPDFModal, setShowPDFModal] = useState(false)
  const [showGuideOnlineModal, setShowGuideOnlineModal] = useState(false)
  const [error, setError] = useState(null)
  const [fetchingDocument, setFetchingDocument] = useState(null)
  const [options, setOptions] = useState([])
  const [cases, setCases] = useState([])
  const [cancellationOutcome, setCancellationOutcome] = useState([])
  const { loading, data, refetch } = useQuery(Queries.getSelfService(booking.bookingNumber))
  const { loading: atiLoading, data: atiData } = useQuery(Queries.getAtiStatus(booking.bookingNumber))
  const [cancelling, setCancelling] = useState(false)
  const product = booking.products[0]
  const isHotelOnly = booking.packageType === 'HOTEL_ONLY'
  const sourceSystem = booking.packageType === 'HOTEL_ONLY' && product.productId && product.productId.startsWith('A') ? 'TRIPS' : 'LEGACY'; 
  const isAtcomBooking = booking.bookingSystem && booking.bookingSystem === 'ATCOMRES'
  const isFlightOnlyMountainBooking = product && product.countryId === 'G-000000555' && product.productType === 'FLIGHT_ONLY'
  const isAskGuidesOpen = booking.guideOnline && booking.guideOnline.open
  const unreadGuideOnlineMessages = isAskGuidesOpen ? booking.guideOnline.messages.filter((message) => message.fromTui && !message.delivered).length : 0
  const cancelBooking = useMutation(Mutations.cancelBooking, {
    update: (proxy, mutationResult) => {
      setCancelling(false)
      if (mutationResult.data.cancelBooking) {
        cancelledCallback()
      } else {
        setCancellationOutcome(getText('selfservice', 'cancelHotel', 'errorMessage'))
      }
    },
  })
  useEffect(() => {
    if (!loading && data.selfService) {
      setIsAvailable(data.selfService.isAvailable)
      setOptions([
        ...data.selfService.options,
        {
          id: '0',
          title: getText('selfservice', 'newCase', 'options', 'otherTitle'),
          description: getText('selfservice', 'newCase', 'options', 'otherDescription'),
        },
      ])
      setCases(
        data.selfService.cases.sort((a, b) => {
          if (a.status === 'closed_case' && b.status !== 'closed_case') return 1
          if (a.status !== 'closed_case' && b.status === 'closed_case') return -1
          return moment(b.created).diff(moment(a.created))
        })
      )
    }
  }, [data])

  useEffect(() => {
    if (!atiLoading && atiData.atiStatus) {
      setAtiStatus(atiData.atiStatus.ShouldBeFilledIn)
    }
  }, [atiData])

  const handleDownloadPDF = async (type) => {
    setError(null)
    setFetchingDocument({ type: type, status: true })
    try {
      const response = await fetchr(`${settings.apiAuthorizedRestUrl}/traveldocument/${booking.bookingNumber}/${type}`, false).get()

      if (response.status === 204) {
        type === 'ticket' ? setError(getText('selfservice', 'ticket', 'notFound')) : setError(getText('selfservice', 'confirm', 'notFound'))
        setFetchingDocument(null)
      } else {
        const filename = response.headers
          .get('content-disposition')
          .split(';')
          .find((n) => n.includes('filename='))
          .replace('filename=', '')
          .trim()

        const blob = await response.blob()
        saveAs(blob, filename)
        setFetchingDocument(null)
      }
    } catch (err) {
      setFetchingDocument(null)
      setError(getText('errors', 'general'))
    }
  }

  const handleCloseCasesModal = () => {
    setShowCasesModal(false)
  }

  const handleShowCasesModal = () => {
    ga.buttonClick('selfservice-cases-show')
    setShowCasesModal(true)
  }

  const handleCloseCreateModal = () => {
    setShowCreateModal(false)
    refetch()
  }

  const handleCreateCase = () => {
    setShowCreateModal(true)
    setShowCasesModal(false)
  }

  const handleShowHotelModal = () => {
    setShowHotelModal(true)
  }

  const handleCloseHotelModal = () => {
    setShowHotelModal(false)
  }

  const handleShowPDFModal = () => {
    setShowPDFModal(true)
  }

  const handleClosePDFModal = () => {
    setShowPDFModal(false)
  }

  const handleCloseGuideOnline = () => {
    setShowGuideOnlineModal(false)
  }

  const showDestination = () => {
    ga.buttonClick('mydestination')
    window.open(`${getText('selfservice', 'destination', 'href')}/${booking.products[0].destinationCode || ''}`, '_blank')
  }

  const showGuideOnline = () => {
    ga.buttonClick('guideonline-show')
    setShowGuideOnlineModal(true)
  }

  const showMountainTransfer = () => {
    window.location.href = getText('selfservice', 'mountainTransfer', 'href')
  }

  const showHotelDetailsButton = () => {
    return booking.products[0] && booking.products[0].hotelContact
  }

  const renderSelfServiceModal = () => {
    return (
      <Modal open={showCreateModal} width={'650'} onClose={handleCloseCreateModal}>
        <SelfServiceNewCase booking={booking} options={options} />
      </Modal>
    )
  }

  const renderCasesModal = () => {
    return (
      <Modal open={showCasesModal} width={'650'} onClose={handleCloseCasesModal}>
        <SelfServiceCases booking={booking} cases={cases} options={options} onCreateCase={handleCreateCase} />
      </Modal>
    )
  }

  const renderHotelInfoModal = () => {
    return (
      <Modal open={showHotelModal} width={'650'} onClose={handleCloseHotelModal}>
        <HotelContactDetails product={booking.products[0]} />
      </Modal>
    )
  }

  const renderPDFModal = () => {
    return (
      <Modal open={showPDFModal} width={'650'} onClose={handleClosePDFModal}>
        <TravelDocuments booking={booking} handleClick={handleDownloadPDF} error={error} loading={fetchingDocument} />
      </Modal>
    )
  }

  const renderGuideOnline = () => {
    return (
      <Modal open={showGuideOnlineModal} width={'800'} onClose={handleCloseGuideOnline}>
        <GuideOnline bookingNumber={booking.bookingNumber} messages={booking.guideOnline.messages} />
      </Modal>
    )
  }

  const isFreeOfCancelCharges = () => {
    let cancellationRules = booking.products[0].cancellationRules
    if (cancellationRules) {
      const freeCancellationRule = cancellationRules.find((rule) => rule.amount === 0)
      
      if (!freeCancellationRule) {
        return false
      }
      let freeDate = moment(freeCancellationRule.to.replace('Z', '')).subtract(5, 'd')

      if (moment().isBefore(freeDate)) {
        return true
      }
    }
    return false
  }

  const shouldShowCancelButton = () => {
    if (!isHotelOnly) {
      return false
    }
    return isFreeOfCancelCharges()
  }

  const getAtiToolTip = () => {
    if (!atiFilledIn) {
      return getText('selfservice', 'ati', 'tooltip')
    }
    return getText('selfservice', 'ati', 'filledInToolTip')
  }

  const handleCancelBooking = () => {
    setCancelling(true)
    setCancellationOutcome(null)
    cancelBooking({
      variables: {
        bookingNumber: booking.bookingNumber,
        sourceSystem: sourceSystem
      },
    })
  }

  if (loading || atiLoading) {
    return null
  }

  return (
    <Fragment>
      {showCasesModal && renderCasesModal()}
      {showCreateModal && renderSelfServiceModal()}
      {showHotelModal && renderHotelInfoModal()}
      {showPDFModal && renderPDFModal()}
      {showGuideOnlineModal && renderGuideOnline()}
      {isAvailable || atiStatus || (tests.GDN && isHotelOnly) || isAtcomBooking ? (
        <Container id="self-service-portal">
          {isAvailable && !shouldShowCancelButton() && (
            <Button onClick={handleShowCasesModal}>
              <Svg icon="user-chat" />
              <strong>{getText('selfservice', 'cases', 'needHelp')}</strong>
              <span>&nbsp;({cases.length})</span>
            </Button>
          )}
          {shouldShowCancelButton() && <CancelBooking bookingNumber={booking.bookingNumber} sourceSystem={sourceSystem} cancelBooking={handleCancelBooking} />}
          {tests.ATI && atiStatus && (
            <Button>
              <Tooltip text={getAtiToolTip()}>
                <a href={`${urls.ati}/${booking.bookingNumber}`}>
                  <strong>{getText('selfservice', 'ati', 'title')}</strong>
                </a>
                {atiFilledIn && <Svg icon="checkmark" />}
              </Tooltip>
            </Button>
          )}
          {tests.GDN && isHotelOnly && showHotelDetailsButton() && (
            <>
              <Button onClick={handleShowHotelModal}>
                <strong>{getText('accommodationOnly', 'hotelInfo', 'contact')}</strong>
              </Button>
            </>
          )}
          <Button onClick={handleShowPDFModal}>
            <strong>{getText('accommodationOnly', 'bookingConfirmation')}</strong>
          </Button>
          {isFlightOnlyMountainBooking && (
            <Button onClick={showMountainTransfer}>
              <strong>{getText('selfservice', 'mountainTransfer', 'title')}</strong>
            </Button>
          )}
          {isAskGuidesOpen && (
            <Button onClick={showGuideOnline}>
              {unreadGuideOnlineMessages > 0 && <NotificationCount>{unreadGuideOnlineMessages}</NotificationCount>}
              <strong>{getText('selfservice', 'askTheGuides', 'title')}</strong>
            </Button>
          )}
          {booking.products[0] && booking.products[0].destinationCode && (
            <Button onClick={showDestination}>
              <strong>{getText('selfservice', 'destination', 'title')}</strong>
            </Button>
          )}
          {cancelling && (
            <CancellingDiv>
              <img src="/cdn/img/loaders/loader-32x32.gif" alt="loading" />
            </CancellingDiv>
          )}
          {cancellationOutcome && <CancellingDiv>{cancellationOutcome}</CancellingDiv>}
        </Container>
      ) : null}
    </Fragment>
  )
}
